import { Typography } from "@mui/material";
import {
  OrderClientStatus,
  OrderDto,
  OrderPaymentFrequency,
  OrderPaymentType,
  StepType,
} from "Api/Api";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { OrderDetail } from "Components/Orders/OrderDetail";
import { OrderDraft } from "Components/Orders/OrderDraft";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { getEnterpriseServicesReset } from "State/Enterprises/Services/ServicesSlice";
import { getPaymentCalendarItemsReset } from "State/OrderWorkflow/Slices/GetPaymentCalendarItemsSlice";
import {
  getOrderDetailAsync,
  getOrderDetailReset,
} from "State/Orders/Detail/OrderDetailSlice";
import { getOrderServicesReset } from "State/Orders/Detail/OrderServicesSlice";
import { RootStateType } from "State/Store";
import { useResource, Resources } from "Translations/Resources";
import { toLocalTimeZoneString } from "Utils/DateUtils";
import { orderPaymentFrequencyToMonths } from "Utils/Order/PaymentFrequencyUtils";
import { addMonths } from "date-fns";
import { cloneDeep } from "lodash-es";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router";

export const DetailPage: React.FunctionComponent = () => {
  const { t } = useResource();
  const params = useParams();
  const { id: publicID } = params;

  const location = useLocation();
  const isCloning =
    new URLSearchParams(location.search).get("isCloning") === "true";

  const isCopying =
    new URLSearchParams(location.search).get("isCopying") === "true";

  const dispatch = useDispatch();
  useEffect(() => {
    if (!!publicID) {
      dispatch(
        getOrderDetailAsync.request({
          publicID,
        }),
      );
    }

    return () => {
      if (!publicID) return;

      dispatch(getOrderDetailReset());
      dispatch(getOrderServicesReset());
      dispatch(getEnterpriseServicesReset());
      dispatch(getPaymentCalendarItemsReset());

      dispatch(getOrderDetailAsync.request({ publicID }));
    };
  }, [dispatch, publicID, location.key]);

  const { data, error } = useSelector((e: RootStateType) => e.order.detail);

  const currentStepType = data?.currentStepType;

  const order = !!data
    ? isCloning
      ? clone(data)
      : isCopying
        ? copy(data)
        : data
    : data;

  return (
    <AuthenticatedLayout
      title={
        isCloning
          ? t(Resources.Orders.Detail.CreateTitle)
          : isCopying
            ? t(Resources.Orders.Detail.CopyTitle)
            : t(Resources.Orders.Detail.DraftTitle, { number: order?.number })
      }
    >
      <StyledPageWrapper
        $isNarrow={currentStepType === StepType.Draft || isCloning || isCopying}
      >
        {error?.cause === 404 && (
          <Typography variant="h2" textAlign={"center"}>
            {t(Resources.Orders.Detail.NotFound)}
          </Typography>
        )}
        {!!currentStepType && !!publicID && (
          <>
            {currentStepType === StepType.Draft || isCloning || isCopying ? (
              <OrderDraft
                publicID={publicID}
                isLoading={false}
                order={order ?? null}
                clonedOrderPublicID={isCloning ? publicID : null}
                copiedFromOrderPublicID={isCopying ? publicID : null}
              />
            ) : (
              <OrderDetail publicID={publicID} />
            )}
          </>
        )}
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

function clone(order: OrderDto): OrderDto {
  const cloned = cloneDeep(order);

  cloned.orderID = 0;
  cloned.publicID = "";
  cloned.clientStatus = OrderClientStatus.Existing;

  return cloned;
}

function copy(order: OrderDto): OrderDto {
  const copied = cloneDeep(order);

  copied.orderID = 0;
  copied.publicID = "";
  copied.clientStatus = OrderClientStatus.Existing;

  if (copied.paymentFrequency !== OrderPaymentFrequency.Single) {
    const originPaymentFrequencyDateFrom = new Date(
      copied.paymentFrequencyDateFrom!,
    );

    const lastPaidCalendarItemInvoiceDate =
      !!copied.lastPaidCalendarItemInvoiceDate
        ? new Date(copied.lastPaidCalendarItemInvoiceDate!)
        : null;

    //kdyz neni zadna polozka zaplacena, tak bereme puvodni zacatek obdobi

    let newPaymentFrequencyDateFrom =
      lastPaidCalendarItemInvoiceDate ?? originPaymentFrequencyDateFrom;

    //kdyz je nejaka polozka zaplacena a je to rpedplacena obejdnavka, pridavame pocet mesici ktere ma zvolna frekvence
    if (
      lastPaidCalendarItemInvoiceDate != null &&
      order.paymentType === OrderPaymentType.Prepaid
    ) {
      const paymentFrequencyMonths = orderPaymentFrequencyToMonths(
        order.paymentFrequency,
      );

      newPaymentFrequencyDateFrom = addMonths(
        newPaymentFrequencyDateFrom,
        paymentFrequencyMonths,
      );
    }
    copied.paymentFrequencyDateFrom = toLocalTimeZoneString(
      newPaymentFrequencyDateFrom,
    );
  }

  return copied;
}
